import { useState } from 'react';
import { motion } from 'framer-motion';
import BlogCard from '../components/BlogCard';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { blogPosts } from '../data/staticData';

const BlogPage = () => {
  const [activeTag, setActiveTag] = useState('all');

  const filteredPosts = activeTag === 'all'
    ? blogPosts
    : blogPosts.filter((post) => post.tags.includes(activeTag));

  const allTags = ['all', ...new Set(blogPosts.flatMap((post) => post.tags))];

  return (
    <div className="min-h-screen pt-24" data-testid="blog-page">
      <SEO
        title="Blog &amp; Event Inspiration"
        description="Event ideas, wedding décor trends, planning tips, and inspiration from Aruma Events."
        path="/blog"
        schema={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.aurmarentals.com/" },
            { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://www.aurmarentals.com/blog" }
          ]
        }}
      />
      {/* Header */}
      <section className="section-padding bg-muted">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <p className="font-body text-primary text-sm uppercase tracking-widest mb-2">
              Blog & Inspiration
            </p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4">
              Event Ideas & Tips
            </h1>
            <p className="font-body text-muted-foreground text-lg">
              Get inspired with Aruma Events' latest articles on event trends, planning tips,
              and décor ideas to make your celebration unforgettable.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Blog Content */}
      <section className="section-padding">
        <div className="container-custom">
          {/* Tag Filter */}
          <div className="flex flex-wrap gap-2 mb-10" data-testid="blog-filters">
            {allTags.map((tag) => (
              <Button
                key={tag}
                variant={activeTag === tag ? 'default' : 'outline'}
                size="sm"
                className={`rounded-full capitalize ${
                  activeTag === tag 
                    ? 'bg-primary text-primary-foreground' 
                    : 'text-foreground hover:bg-primary/10'
                }`}
                onClick={() => setActiveTag(tag)}
                data-testid={`blog-filter-${tag}`}
              >
                {tag === 'all' ? 'All Posts' : tag}
              </Button>
            ))}
          </div>

          {/* Blog Grid */}
          {filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="font-body text-muted-foreground">No posts found with this tag.</p>
              <Button 
                variant="link" 
                onClick={() => setActiveTag('all')}
                className="mt-2 text-primary"
              >
                View all posts
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, index) => (
                <BlogCard 
                  key={post.id} 
                  post={post} 
                  index={index}
                  featured={index === 0 && activeTag === 'all'}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-16 bg-muted">
        <div className="container-custom text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-4">
            Stay Inspired
          </h2>
          <p className="font-body text-muted-foreground mb-6 max-w-xl mx-auto">
            Subscribe to our newsletter for the latest event trends and exclusive tips.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-full border border-border focus:outline-none focus:border-primary focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              data-testid="newsletter-email"
            />
            <Button 
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6"
              data-testid="newsletter-submit"
            >
              Subscribe
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
